import React, { ReactNode, createContext, useContext, useState } from 'react';

interface PanelConfig {
    id: string;
    content: ReactNode;
    title?: string;
    placement?: 'start' | 'end' | 'top' | 'bottom';
    width?: string;
    backdrop?: boolean;
}

interface PanelContextType {
    openPanel: (config: PanelConfig) => void;
    closePanel: (id?: string) => void;
    activePanels: PanelConfig[];
}

const PanelContext = createContext<PanelContextType | undefined>(undefined);

export const usePanelContext = () => {
    const context = useContext(PanelContext);
    if (!context) throw new Error('usePanel must be used within PanelProvider');
    return context;
};

export const PanelProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [activePanels, setActivePanels] = useState<PanelConfig[]>([]);

    const openPanel = (config: PanelConfig) => {
        setActivePanels((prev) => {
            // Prevent duplicates
            if (prev.find((p) => p.id === config.id)) return prev;
            return [...prev, config];
        });
    };

    const closePanel = (id?: string) => {
        if (id) {
            setActivePanels((prev) => prev.filter((p) => p.id !== id));
        } else {
            setActivePanels([]);
        }
    };

    return (
        <PanelContext.Provider value={{ openPanel, closePanel, activePanels }}>
            {children}
        </PanelContext.Provider>
    );
};
